import { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Truck, PackageCheck, X, ArrowLeftRight } from "lucide-react";
import { usePinPrompt } from "@/components/app/PinPrompt";
import TransferDialog from "@/components/app/TransferDialog";

const daysLate = (d) => (d ? Math.floor((new Date() - new Date(d)) / 86400000) : null);

export default function StockTransit() {
  const pinPrompt = usePinPrompt();
  const [data, setData] = useState({ transfers: [], purchase_orders: [] });
  const [whs, setWhs] = useState([]);
  const [xfer, setXfer] = useState(false);
  const load = async () => {
    const [d, w] = await Promise.all([api.get("/stock-in-transit"), api.get("/warehouses")]);
    setData(d.data); setWhs(w.data);
  };
  useEffect(() => { load(); }, []);
  const whName = (id) => whs.find((w) => w._id_str === id)?.name || "Unassigned";

  const act = async (url, msg) => {
    await pinPrompt();
    try { await api.post(url); toast.success(msg); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const Late = ({ d }) => { const n = daysLate(d); return n > 0 ? <span className="chip chip-red ml-1">{n}d late</span> : null; };

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="overline">Monitoring</div>
          <h1 className="text-3xl font-bold tracking-tight">Stock in Transit</h1>
          <p className="text-sm text-muted-foreground">Warehouse transfers dispatched but not received, and supplier orders marked in transit.</p>
        </div>
        <Button className="btn-primary" onClick={() => setXfer(true)} disabled={!whs.length} data-testid="transit-new-transfer"><ArrowLeftRight size={14} /> New Transfer</Button>
      </div>
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-border rounded-lg p-5"><div className="overline">Transfers in transit</div><div className="text-3xl font-mono font-bold mt-2 text-blue-800" data-testid="transit-transfer-count">{data.transfers.length}</div></div>
        <div className="bg-white border border-border rounded-lg p-5"><div className="overline">Supplier orders in transit</div><div className="text-3xl font-mono font-bold mt-2 text-blue-800" data-testid="transit-po-count">{data.purchase_orders.length}</div></div>
        <div className="bg-white border border-border rounded-lg p-5"><div className="overline">Inbound order value</div><div className="text-3xl font-mono font-bold mt-2">{fmtINR(data.po_value)}</div></div>
      </div>
      <div className="overline mb-2 flex items-center gap-2"><ArrowLeftRight size={12} /> Warehouse transfers</div>
      <div className="bg-white border border-border rounded-lg overflow-x-auto mb-6">
        <table className="acc-table w-full">
          <thead><tr><th>No.</th><th>Dispatched</th><th>From → To</th><th>Items</th><th>Vehicle</th><th>Expected</th><th>Actions</th></tr></thead>
          <tbody>
            {data.transfers.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Nothing in transit.</td></tr>}
            {data.transfers.map((t) => (
              <tr key={t._id_str} data-testid={`transit-row-${t._id_str}`}>
                <td className="font-mono">{t.number}</td>
                <td>{new Date(t.dispatched_at).toLocaleDateString()}</td>
                <td>{whName(t.from_warehouse_id)}{t.from_bin ? ` (${t.from_bin})` : ""} → <b>{whName(t.to_warehouse_id)}</b>{t.to_bin ? ` (${t.to_bin})` : ""}</td>
                <td className="text-xs">{t.lines.map((l) => `${l.name} × ${l.qty}`).join(", ")}</td>
                <td>{t.vehicle_number || t.transporter || "—"}</td>
                <td>{t.expected_date || "—"}<Late d={t.expected_date} /></td>
                <td><div className="flex gap-1">
                  <button className="p-1 hover:bg-emerald-50 text-emerald-700 rounded" title="Mark received" onClick={() => act(`/stock-transfers/${t._id_str}/receive`, "Received")} data-testid={`transit-receive-${t._id_str}`}><PackageCheck size={14} /></button>
                  <button className="p-1 hover:bg-red-50 text-red-600 rounded" title="Cancel (return to source)" onClick={() => act(`/stock-transfers/${t._id_str}/cancel`, "Cancelled")} data-testid={`transit-cancel-${t._id_str}`}><X size={14} /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="overline mb-2 flex items-center gap-2"><Truck size={12} /> Purchase orders in transit</div>
      <div className="bg-white border border-border rounded-lg overflow-x-auto">
        <table className="acc-table w-full">
          <thead><tr><th>PO</th><th>Supplier</th><th>Dispatched</th><th>Transporter</th><th>Expected</th><th className="num">Value</th><th>Actions</th></tr></thead>
          <tbody>
            {data.purchase_orders.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">No supplier orders in transit.</td></tr>}
            {data.purchase_orders.map((p) => (
              <tr key={p._id_str} data-testid={`transit-po-${p._id_str}`}>
                <td className="font-mono">{p.number}</td>
                <td>{p.party_name}</td>
                <td>{p.dispatched_at ? new Date(p.dispatched_at).toLocaleDateString() : "—"}</td>
                <td>{p.transporter || "—"}</td>
                <td>{p.expected_date?.slice(0, 10) || "—"}<Late d={p.expected_date} /></td>
                <td className="num">{fmtINR(p.total)}</td>
                <td><button className="p-1 hover:bg-emerald-50 text-emerald-700 rounded" title="Receive goods" onClick={() => act(`/txns/${p._id_str}/receive`, "Received → Purchase bill created")} data-testid={`transit-po-receive-${p._id_str}`}><PackageCheck size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TransferDialog open={xfer} onClose={() => setXfer(false)} warehouses={whs} onDone={load} />
    </div>
  );
}
